import {
  Component,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from "react";
import { react } from "@tldraw/state";
import { useValue } from "@tldraw/state-react";
import { GeoShapeGeoStyle, type TLHandle, type TLShape, type TLShapeId } from "@tldraw/tlschema";
import { Box, Vec, type VecLike } from "../geometry";
import { CanvasEditorContext } from "../editor-context";
import { CLIPBOARD_TAG, type CanvasEditor } from "./editor";
import { Mat } from "./math";
import { playEntrance, takeEntrance } from "./shape-entrance";
import type { ResizeHandle } from "./shape-util";
import type { PointerEventInfo, PointerTarget } from "./state-node";
import "./canvas.css";

const CULL_MARGIN = 400; // page units kept mounted beyond the viewport
const HANDLE_SIZE = 8; // screen px
const ROTATE_OFFSET = 22; // screen px above the selection
const DOUBLE_CLICK_MS = 450;

/** Planroom's canvas: shapes, overlays and input for one editor. */
export function CanvasView({
  editor,
  children,
}: {
  editor: CanvasEditor;
  children?: ReactNode;
}) {
  const container = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = container.current!;
    editor.setContainer(el);
    const update = () => editor.updateViewportScreenBounds(el);
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      editor.setContainer(null);
    };
  }, [editor]);
  useInput(editor, container);
  const camera = useValue("camera", () => editor.getCamera(), [editor]);
  const cursor = useValue(
    "cursor",
    () => editor.getInstanceState().cursor.type,
    [editor],
  );
  return (
    <CanvasEditorContext.Provider value={editor}>
      <div
        ref={container}
        className="canvas-view"
        tabIndex={0}
        data-cursor={cursor}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className="canvas-layer"
          style={{
            transform: `scale(${camera.z}) translate(${camera.x}px, ${camera.y}px)`,
            // Lets screen-constant strokes (e.g. the text editing frame) undo the zoom.
            ["--canvas-zoom" as string]: camera.z,
          }}
        >
          <Shapes editor={editor} />
        </div>
        <Overlay editor={editor} />
        <Cursors editor={editor} />
        {children}
      </div>
    </CanvasEditorContext.Provider>
  );
}

function Shapes({ editor }: { editor: CanvasEditor }) {
  // Joined so the list only re-renders when membership or order changes.
  const visible = useValue(
    "visible shapes",
    () => {
      const view = editor.getViewportPageBounds().expandBy(CULL_MARGIN);
      const editing = editor.getEditingShapeId();
      return editor
        .getCurrentPageShapesSorted()
        .filter((shape) => {
          if (shape.id === editing) return true;
          const bounds = editor.getShapePageBounds(shape);
          return !!bounds && bounds.collides(view);
        })
        .map((s) => s.id)
        .join("\n");
    },
    [editor],
  );
  return (
    <>
      {visible
        ? visible
            .split("\n")
            .map((id) => (
              <ShapeView key={id} editor={editor} id={id as TLShapeId} />
            ))
        : null}
    </>
  );
}

const ShapeView = memo(function ShapeView({
  editor,
  id,
}: {
  editor: CanvasEditor;
  id: TLShapeId;
}) {
  const shape = useValue("shape", () => editor.getShape(id), [editor, id]);
  const transform = useValue(
    "shape transform",
    () => editor.getShapePageTransform(id).toCss(),
    [editor, id],
  );
  const clipPath = useValue("shape clip", () => clipFor(editor, id), [
    editor,
    id,
  ]);
  const hidden = useValue("shape hidden", () => editor.isShapeHidden(id), [
    editor,
    id,
  ]);
  const erasing = useValue(
    "shape erasing",
    () => editor.getErasingShapeIds().some((e) => e === id || editor.hasAncestor(id, e)),
    [editor, id],
  );
  const size = useValue(
    "shape size",
    () => {
      const b = editor.getShapeGeometry(id).bounds;
      return `${Math.max(1, b.maxX)}x${Math.max(1, b.maxY)}`;
    },
    [editor, id],
  );
  const element = useRef<HTMLDivElement>(null);
  const mounted = !!shape && !hidden;
  useLayoutEffect(() => {
    if (!mounted || !element.current) return;
    const entrance = takeEntrance(id);
    if (entrance) playEntrance(element.current, entrance);
  }, [mounted, id]);
  if (!shape || hidden) return null;
  const [w, h] = size.split("x").map(Number);
  return (
    <div
      ref={element}
      className="canvas-shape"
      data-shape-id={id}
      data-shape-type={shape.type}
      style={{
        transform,
        opacity: shape.opacity * (erasing ? 0.3 : 1),
        clipPath,
        width: w,
        height: h,
      }}
    >
      <ShapeBoundary type={shape.type}>
        <ShapeContent editor={editor} shape={shape} />
      </ShapeBoundary>
    </div>
  );
});

function ShapeContent({
  editor,
  shape,
}: {
  editor: CanvasEditor;
  shape: TLShape;
}) {
  const util = editor.getUtil(shape);
  const cropping = useValue(
    "shape cropping",
    () => editor.getCroppingShapeId() === shape.id,
    [editor, shape.id],
  );
  return (
    <>
      {cropping && (
        <div className="canvas-crop-preview">
          {(util as { cropPreview?(s: TLShape): ReactNode }).cropPreview?.(shape)}
        </div>
      )}
      {util.component(shape)}
    </>
  );
}

/** A shape that fails to render shows a placeholder instead of breaking the board. */
class ShapeBoundary extends Component<
  { type: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.warn(`Shape "${this.props.type}" could not be drawn`, error);
  }
  render() {
    return this.state.failed ? (
      <div className="canvas-shape-error">
        This {this.props.type} can’t be shown here yet
      </div>
    ) : (
      this.props.children
    );
  }
}

/** CSS clip polygon for a shape whose ancestors clip their children. */
function clipFor(editor: CanvasEditor, id: TLShapeId): string | undefined {
  const polygon = editor.getShapeClipPolygon(id);
  if (!polygon) return undefined;
  const toLocal = editor.getShapePageTransform(id).invert();
  return `polygon(${polygon
    .map((p) => toLocal.applyToPoint(p))
    .map((p) => `${p.x}px ${p.y}px`)
    .join(", ")})`;
}

// ----- selection geometry shared by drawing and hit testing -----

type SelectionFrame = {
  /** Corners in screen space: top-left, top-right, bottom-right, bottom-left. */
  corners: Vec[];
  rotation: number;
  resizable: boolean;
  rotatable: boolean;
};

export function selectionFrame(editor: CanvasEditor): SelectionFrame | null {
  const selected = editor.getSelectedShapes();
  if (!selected.length || editor.getEditingShapeId()) return null;
  const toScreen = (p: VecLike) => editor.pageToScreen(p);
  const origin = editor.getViewportScreenBounds();
  const local = (p: Vec) => new Vec(p.x - origin.x, p.y - origin.y);
  if (selected.length === 1) {
    const shape = selected[0];
    const util = editor.getUtil(shape);
    const bounds = editor.getShapeGeometry(shape).bounds;
    const corners = editor
      .getShapePageTransform(shape)
      .applyToPoints(Box.From(bounds).corners)
      .map((p) => local(toScreen(p)));
    return {
      corners,
      rotation: editor.getShapePageTransform(shape).rotation(),
      resizable:
        util.canResize?.(shape) !== false && !util.hideResizeHandles?.(shape),
      rotatable: !util.hideRotateHandle?.(shape),
    };
  }
  const bounds = editor.getSelectionPageBounds();
  if (!bounds) return null;
  return {
    corners: Box.From(bounds).corners.map((p) => local(toScreen(p))),
    rotation: 0,
    resizable: selected.every(
      (s) => editor.getUtil(s).canResize?.(s) !== false,
    ),
    rotatable: false,
  };
}

/** Resize handle positions in screen space. */
function handlePoints(frame: SelectionFrame): Array<[ResizeHandle, Vec]> {
  const [tl, tr, br, bl] = frame.corners;
  const mid = (a: Vec, b: Vec) => Vec.Lrp(a, b, 0.5);
  return [
    ["top_left", tl],
    ["top", mid(tl, tr)],
    ["top_right", tr],
    ["right", mid(tr, br)],
    ["bottom_right", br],
    ["bottom", mid(br, bl)],
    ["bottom_left", bl],
    ["left", mid(bl, tl)],
  ];
}
function rotatePoint(frame: SelectionFrame) {
  const [tl, tr] = frame.corners;
  const top = Vec.Lrp(tl, tr, 0.5);
  const up = new Vec(Math.sin(frame.rotation), -Math.cos(frame.rotation));
  return top.add(up.mul(ROTATE_OFFSET));
}

/** Shape handles (line ends, polygon corners) of the only selected shape. */
function shapeHandles(
  editor: CanvasEditor,
): Array<{ shape: TLShape; handle: TLHandle; screen: Vec }> {
  const shape = editor.getOnlySelectedShape();
  if (!shape || editor.getEditingShapeId()) return [];
  const handles = editor.getUtil(shape).getHandles?.(shape) as
    TLHandle[] | undefined;
  if (!handles?.length) return [];
  const origin = editor.getViewportScreenBounds();
  const transform = editor.getShapePageTransform(shape);
  return handles.map((handle) => {
    const p = editor.pageToScreen(transform.applyToPoint(handle));
    return { shape, handle, screen: new Vec(p.x - origin.x, p.y - origin.y) };
  });
}

// ----- overlay -----

function Overlay({ editor }: { editor: CanvasEditor }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let frame = 0;
    const stop = react("canvas overlay", () => {
      // Read everything the drawing depends on so changes schedule a redraw.
      editor.getCamera();
      editor.getViewportScreenBounds();
      editor.getSelectedShapeIds().forEach((id) => editor.getShape(id));
      editor.getHoveredShapeId();
      editor.getHintingShapeIds();
      editor.getEditingShapeId();
      editor.getInstanceState().brush;
      editor.getSnapLines();
      editor.isIn("select");
      for (const id of editor.getSelectedShapeIds())
        if (editor.getShape(id)) editor.getShapePageTransform(id);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => draw(editor, canvas.current));
    });
    return () => {
      stop();
      cancelAnimationFrame(frame);
    };
  }, [editor]);
  return <canvas ref={canvas} className="canvas-overlay" />;
}

function draw(editor: CanvasEditor, canvas: HTMLCanvasElement | null) {
  if (!canvas) return;
  const ui = (editor.getCurrentTheme() as unknown as { colors: { light: Record<string, string | undefined> } })
    .colors.light;
  const SELECTION = ui.selectionStroke ?? "#2f80ed";
  const { w, h } = editor.getViewportScreenBounds();
  const dpr = window.devicePixelRatio || 1;
  if (
    canvas.width !== Math.round(w * dpr) ||
    canvas.height !== Math.round(h * dpr)
  ) {
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const { x, y, z } = editor.getCamera();
  const camera = new Mat(z * dpr, 0, 0, z * dpr, x * z * dpr, y * z * dpr);

  const outline = (id: TLShapeId, color: string, width: number) => {
    const shape = editor.getShape(id);
    if (!shape || editor.isShapeHidden(shape)) return;
    const m = camera.multiply(editor.getShapePageTransform(shape));
    ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f);
    ctx.strokeStyle = color;
    ctx.lineWidth = (width * dpr) / (z * dpr);
    const raw = editor.getUtil(shape).getIndicatorPath?.(shape) as
      Path2D | string | { path: Path2D } | undefined;
    const path =
      typeof raw === "string"
        ? new Path2D(raw)
        : raw && "path" in (raw as object) && !(raw instanceof Path2D)
          ? (raw as { path: Path2D }).path
          : ((raw as Path2D | undefined) ??
            new Path2D(editor.getShapeGeometry(shape).toSvgPath?.() ?? ""));
    ctx.stroke(path);
  };

  const editing = editor.getEditingShapeId();
  for (const id of editor.getHintingShapeIds()) outline(id, SELECTION, 2.5);
  const hovered = editor.getHoveredShapeId();
  if (
    hovered &&
    !editor.getSelectedShapeIds().includes(hovered) &&
    editor.isIn("select.idle")
  )
    outline(hovered, SELECTION, 1.5);
  for (const id of editor.getSelectedShapeIds())
    if (id !== editing) outline(id, SELECTION, 1.5);

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const brush = editor.getInstanceState().brush;
  if (brush) {
    const tl = editor.pageToScreen({ x: brush.x, y: brush.y });
    const origin = editor.getViewportScreenBounds();
    ctx.fillStyle = ui.brushFill ?? ui.selectionFill ?? "rgba(47, 128, 237, 0.06)";
    ctx.strokeStyle = ui.brushStroke ?? SELECTION;
    ctx.lineWidth = 1;
    const rect: [number, number, number, number] = [
      tl.x - origin.x,
      tl.y - origin.y,
      brush.w * z,
      brush.h * z,
    ];
    ctx.fillRect(...rect);
    ctx.strokeRect(...rect);
  }
  // Alignment guides: a line through the aligned edges and centres, marked at each.
  const snaps = editor.getSnapLines();
  if (snaps.length) {
    const origin = editor.getViewportScreenBounds();
    ctx.strokeStyle = ui.snap ?? "#e24f4f";
    ctx.lineWidth = 1;
    for (const line of snaps) {
      const points = line.points.map((p) => {
        const s = editor.pageToScreen(p);
        return new Vec(s.x - origin.x, s.y - origin.y);
      });
      const span = Box.FromPoints(points);
      ctx.beginPath();
      ctx.moveTo(span.minX, span.minY);
      ctx.lineTo(span.maxX, span.maxY);
      ctx.stroke();
      for (const p of points) {
        ctx.beginPath();
        ctx.moveTo(p.x - 3, p.y - 3);
        ctx.lineTo(p.x + 3, p.y + 3);
        ctx.moveTo(p.x - 3, p.y + 3);
        ctx.lineTo(p.x + 3, p.y - 3);
        ctx.stroke();
      }
    }
  }

  if (!editor.isIn("select") || editor.getIsReadonly()) return;
  const frame = selectionFrame(editor);
  if (frame) {
    ctx.strokeStyle = SELECTION;
    ctx.lineWidth = 1;
    ctx.beginPath();
    frame.corners.forEach((p, i) =>
      i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y),
    );
    ctx.closePath();
    ctx.stroke();
    if (frame.resizable && editor.isIn("select.idle"))
      for (const [, p] of handlePoints(frame)) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(
          p.x - HANDLE_SIZE / 2,
          p.y - HANDLE_SIZE / 2,
          HANDLE_SIZE,
          HANDLE_SIZE,
        );
        ctx.strokeRect(
          p.x - HANDLE_SIZE / 2,
          p.y - HANDLE_SIZE / 2,
          HANDLE_SIZE,
          HANDLE_SIZE,
        );
      }
    if (frame.rotatable && editor.isIn("select.idle")) {
      const r = rotatePoint(frame);
      ctx.beginPath();
      ctx.arc(r.x, r.y, HANDLE_SIZE / 2, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.stroke();
    }
  }
  for (const { screen } of shapeHandles(editor)) {
    ctx.beginPath();
    ctx.arc(screen.x, screen.y, 5, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.strokeStyle = SELECTION;
    ctx.stroke();
  }
}

// ----- collaborator cursors -----

function Cursors({ editor }: { editor: CanvasEditor }) {
  const people = useValue(
    "collaborator cursors",
    () => {
      const origin = editor.getViewportScreenBounds();
      return editor
        .getCollaborators()
        .filter((p) => p.cursor)
        .map((p) => {
          const s = editor.pageToScreen(p.cursor!);
          return {
            id: p.id,
            name: p.userName,
            color: p.color,
            x: s.x - origin.x,
            y: s.y - origin.y,
          };
        });
    },
    [editor],
  );
  return (
    <>
      {people.map((p) => (
        <div
          key={p.id}
          className="canvas-cursor"
          style={{ transform: `translate(${p.x}px, ${p.y}px)`, color: p.color }}
        >
          <svg width="14" height="18" viewBox="0 0 14 18" aria-hidden="true">
            <path
              d="M1 1 L1 15 L5 11 L8 17 L10 16 L7 10 L12 10 Z"
              fill="currentColor"
              stroke="#fff"
            />
          </svg>
          <span style={{ background: p.color }}>{p.name}</span>
        </div>
      ))}
    </>
  );
}

// ----- input -----

function useInput(
  editor: CanvasEditor,
  container: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    const el = container.current!;
    let lastClick = { time: 0, point: new Vec() };
    let panning: {
      from: Vec;
      camera: { x: number; y: number; z: number };
    } | null = null;
    let spaceDown = false;

    const screen = (e: { clientX: number; clientY: number }) =>
      new Vec(e.clientX, e.clientY);
    const modifiers = (e: {
      shiftKey: boolean;
      altKey: boolean;
      ctrlKey: boolean;
      metaKey: boolean;
    }) => ({
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
    });

    /** What the pointer is over: a selection handle, a shape handle, a shape, or empty canvas. */
    const targetAt = (point: Vec): PointerTarget => {
      const origin = editor.getViewportScreenBounds();
      const local = new Vec(point.x - origin.x, point.y - origin.y);
      if (editor.isIn("select") && !editor.getIsReadonly()) {
        for (const { shape, handle, screen } of shapeHandles(editor))
          if (Vec.Dist(screen, local) <= 7)
            return { target: "handle", shape, handle };
        const frame = selectionFrame(editor);
        if (frame) {
          if (
            frame.rotatable &&
            Vec.Dist(rotatePoint(frame), local) <= HANDLE_SIZE
          )
            return { target: "selection", handle: "rotate" };
          if (frame.resizable)
            for (const [handle, p] of handlePoints(frame))
              if (
                Math.abs(p.x - local.x) <= HANDLE_SIZE &&
                Math.abs(p.y - local.y) <= HANDLE_SIZE
              )
                return { target: "selection", handle };
        }
      }
      const page = editor.screenToPage(point);
      const shape = editor.getShapeAtPoint(page, {
        hitInside: true,
        hitLabels: true,
        margin: editor.getHitTestMargin(),
      });
      if (shape) return { target: "shape", shape };
      const frame = selectionFrame(editor);
      if (frame && pointInQuad(local, frame.corners))
        return { target: "selection" };
      return { target: "canvas" };
    };

    const pointerInfo = (
      e: PointerEvent,
      name: PointerEventInfo["name"],
    ): PointerEventInfo => {
      const point = screen(e);
      return {
        type: "pointer",
        name,
        point,
        pagePoint: editor.screenToPage(point),
        button: e.button,
        pointerId: e.pointerId,
        ...modifiers(e),
        ...targetAt(point),
      } as PointerEventInfo;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (
        (e.target as HTMLElement).closest(
          "[data-canvas-ignore], input, textarea, [contenteditable=true]",
        )
      )
        return;
      el.focus({ preventScroll: true });
      // Middle button, right button or Space pans the canvas.
      if (e.button === 1 || e.button === 2 || spaceDown) {
        panning = { from: screen(e), camera: { ...editor.getCamera() } };
        editor.inputs.isPanning = true;
        el.setPointerCapture(e.pointerId);
        editor.setCursor({ type: "grabbing", rotation: 0 });
        e.preventDefault();
        return;
      }
      if (e.button !== 0) return;
      el.setPointerCapture(e.pointerId);
      const info = pointerInfo(e, "pointer_down");
      const now = performance.now();
      const double =
        now - lastClick.time < DOUBLE_CLICK_MS &&
        Vec.Dist(lastClick.point, info.point) < 6;
      lastClick = { time: double ? 0 : now, point: info.point };
      editor.dispatch(info);
      if (double)
        editor.dispatch({
          ...info,
          type: "click",
          name: "double_click",
          phase: "down",
        } as never);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (panning) {
        const d = Vec.Sub(screen(e), panning.from);
        editor.setCamera({
          x: panning.camera.x + d.x / panning.camera.z,
          y: panning.camera.y + d.y / panning.camera.z,
          z: panning.camera.z,
        });
        return;
      }
      editor.dispatch(pointerInfo(e, "pointer_move"));
    };
    const onPointerUp = (e: PointerEvent) => {
      if (el.hasPointerCapture(e.pointerId))
        el.releasePointerCapture(e.pointerId);
      if (panning) {
        panning = null;
        editor.inputs.isPanning = false;
        editor.setCursor({ type: spaceDown ? "grab" : "default", rotation: 0 });
        return;
      }
      if (e.button !== 0) return;
      editor.dispatch(pointerInfo(e, "pointer_up"));
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const point = screen(e);
      const info = {
        type: "wheel" as const,
        name: "wheel" as const,
        delta: new Vec(e.deltaX, e.deltaY),
        point,
        ...modifiers(e),
      };
      editor.dispatch(info);
      const options = editor.getCameraOptions();
      if (options.isLocked) return;
      const pinch = e.ctrlKey || e.metaKey;
      if (pinch || options.wheelBehavior === "zoom") {
        const z =
          editor.getZoomLevel() *
          Math.exp(-e.deltaY * (pinch ? 0.01 : 0.002) * options.zoomSpeed);
        editor.zoomAround(z, point);
      } else {
        const { x, y, z } = editor.getCamera();
        const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
        const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY;
        editor.setCamera({
          x: x - (dx * options.panSpeed) / z,
          y: y - (dy * options.panSpeed) / z,
          z,
        });
      }
    };
    // Digits by their key position, so Shift+1 reads as "shift+1" on any layout.
    const combo = (e: KeyboardEvent) =>
      `${e.metaKey || e.ctrlKey ? "mod+" : ""}${e.shiftKey ? "shift+" : ""}${e.altKey ? "alt+" : ""}${e.code.startsWith("Digit") ? e.code.slice(5) : e.key.toLowerCase()}`;
    const selected = () =>
      editor
        .getSelectedShapeIds()
        .filter((id) => !editor.isShapeOrAncestorLocked(id));
    const tool = (id: string, geo?: string) => () => {
      if (geo) editor.setStyleForNextShapes(GeoShapeGeoStyle, geo as never);
      editor.setCurrentTool(id);
    };
    const edit = (name: string, run: () => void) => () => {
      if (editor.getIsReadonly() || !editor.getSelectedShapeIds().length) return;
      editor.markHistoryStoppingPoint(name);
      run();
    };
    // Single keys and Shift combinations, as boards have always used.
    const keys: Record<string, () => void> = {
      v: tool("select"),
      h: tool("hand"),
      r: tool("geo", "rectangle"),
      o: tool("geo", "ellipse"),
      d: tool("draw"),
      x: tool("draw"),
      e: tool("eraser"),
      a: tool("arrow"),
      t: tool("text"),
      n: tool("note"),
      l: tool("line"),
      "shift+d": tool("highlight"),
      "shift+l": edit("lock", () => editor.toggleLock(editor.getSelectedShapeIds())),
      "[": edit("send backward", () => editor.sendBackward(selected())),
      "]": edit("bring forward", () => editor.bringForward(selected())),
      "shift+1": () => editor.zoomToFit({ animation: { duration: 200 } }),
      "shift+2": () => editor.zoomToSelection({ animation: { duration: 200 } }),
      "shift+0": () => editor.resetZoom(undefined, { animation: { duration: 200 } }),
    };
    const defaults: Record<string, () => void> = {
      "mod+z": () => editor.undo(),
      "mod+shift+z": () => editor.redo(),
      "mod+y": () => editor.redo(),
      "mod+a": () =>
        editor.setSelectedShapes(
          editor
            .getSortedChildIdsForParent(editor.getCurrentPageId())
            .filter(
              (id) =>
                !editor.isShapeOrAncestorLocked(id) &&
                !editor.isShapeHidden(id),
            ),
        ),
      "mod+d": () => {
        if (editor.getSelectedShapeIds().length && !editor.getIsReadonly()) {
          editor.markHistoryStoppingPoint("duplicate");
          editor.duplicateShapes(editor.getSelectedShapeIds());
        }
      },
      "mod+g": edit("group", () => {
        if (selected().length > 1) editor.groupShapes(selected());
      }),
      "mod+shift+g": edit("ungroup", () => {
        const groups = editor.getSelectedShapes().filter((s) => s.type === "group");
        if (groups.length) editor.ungroupShapes(groups.map((s) => s.id));
      }),
      "mod+=": () => editor.zoomIn(undefined, { animation: { duration: 120 } }),
      "mod+-": () => editor.zoomOut(undefined, { animation: { duration: 120 } }),
    };
    const onKey = (e: KeyboardEvent, name: "key_down" | "key_up") => {
      if (
        (e.target as HTMLElement).closest(
          "input, textarea, [contenteditable=true]",
        )
      )
        return;
      if (name === "key_down" && (e.metaKey || e.ctrlKey || e.altKey)) {
        const key = combo(e);
        const custom = editor.getShortcut(key);
        const builtIn = defaults[key];
        if (custom || builtIn) {
          e.preventDefault();
          if (custom) custom(editor);
          else builtIn();
          return;
        }
      }
      // Tool and arrange keys apply when nothing is being typed or dragged.
      if (
        name === "key_down" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.repeat &&
        !editor.getEditingShapeId() &&
        !editor.inputs.isPointerDown
      ) {
        const key = combo(e);
        const custom = editor.getShortcut(key);
        const builtIn = keys[key];
        if (custom || builtIn) {
          e.preventDefault();
          if (custom) custom(editor);
          else builtIn();
          return;
        }
      }
      if (e.code === "Space") {
        spaceDown = name === "key_down";
        if (!panning)
          editor.setCursor({
            type: spaceDown ? "grab" : "default",
            rotation: 0,
          });
        e.preventDefault();
        return;
      }
      editor.dispatch({
        type: "keyboard",
        name: e.repeat && name === "key_down" ? "key_repeat" : name,
        key: e.key,
        code: e.code,
        ...modifiers(e),
      });
    };
    const keydown = (e: KeyboardEvent) => onKey(e, "key_down");
    const keyup = (e: KeyboardEvent) => onKey(e, "key_up");
    // Clipboard and drops. Planroom content travels as tagged JSON text.
    const focused = () => el.contains(document.activeElement);
    const editingText = () =>
      !!(document.activeElement as HTMLElement | null)?.closest(
        "input, textarea, [contenteditable=true]",
      );
    const onCopy = (e: ClipboardEvent, cut = false) => {
      if (!focused() || editingText()) return;
      const ids = editor.getSelectedShapeIds();
      const content = ids.length && editor.getContentFromCurrentPage(ids);
      if (!content) return;
      e.clipboardData?.setData(
        "text/plain",
        CLIPBOARD_TAG + JSON.stringify(content),
      );
      e.preventDefault();
      if (cut && !editor.getIsReadonly()) {
        editor.markHistoryStoppingPoint("cut");
        editor.deleteShapes(ids);
      }
    };
    const copy = (e: ClipboardEvent) => onCopy(e);
    const cut = (e: ClipboardEvent) => onCopy(e, true);
    const pointerPage = () => {
      const p = editor.inputs.getCurrentPagePoint();
      return editor.getViewportPageBounds().containsPoint(p)
        ? p
        : editor.getViewportPageBounds().center;
    };
    const paste = (e: ClipboardEvent) => {
      if (!focused() || editingText() || editor.getIsReadonly()) return;
      e.preventDefault();
      void editor.paste(
        {
          files: [...(e.clipboardData?.files ?? [])],
          text: e.clipboardData?.getData("text/plain") ?? "",
        },
        pointerPage(),
      );
    };
    const dragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      const files = [...(e.dataTransfer?.files ?? [])];
      if (!files.length || editor.getIsReadonly()) return;
      e.preventDefault();
      editor.markHistoryStoppingPoint("drop files");
      void editor.putExternalContent({
        type: "files",
        files,
        point: editor.screenToPage({ x: e.clientX, y: e.clientY }),
      });
    };
    document.addEventListener("copy", copy);
    document.addEventListener("cut", cut);
    document.addEventListener("paste", paste);
    el.addEventListener("dragover", dragOver);
    el.addEventListener("drop", drop);
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("keydown", keydown);
    el.addEventListener("keyup", keyup);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("keydown", keydown);
      el.removeEventListener("keyup", keyup);
      document.removeEventListener("copy", copy);
      document.removeEventListener("cut", cut);
      document.removeEventListener("paste", paste);
      el.removeEventListener("dragover", dragOver);
      el.removeEventListener("drop", drop);
    };
  }, [editor, container]);
}

function pointInQuad(p: Vec, quad: Vec[]) {
  let inside = false;
  for (let i = 0, j = quad.length - 1; i < quad.length; j = i++) {
    const a = quad[i],
      b = quad[j];
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
}
